// CurrentSensorDashboard.js (alphatech)
// Standalone Current Sensor dashboard. Data & endpoints identical to Aquabrim's
// CurrentSensorDisplay (metric maths + api/current-sensor-logs/summary polling),
// presented in alphatech's slate design — KPI hero, PF ring gauge and phase
// utilisation bars — deliberately distinct from Aquabrim's gradient MUI cards.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FaBolt, FaPlug, FaWaveSquare, FaTachometerAlt, FaLeaf, FaClock } from 'react-icons/fa';
import { API_BASE_URL } from '../../../config/config';
import { SLATE, PHASE, SectionCard } from './sensorUi';
import CurrentSensorGraphs from './CurrentSensorGraphs';
import CurrentSensorLogs from './CurrentSensorLogs';

dayjs.extend(relativeTime);

const n2 = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const rel = (t) => (t ? dayjs(t).fromNow() : 'N/A');

// hex -> rgba, for subtle accent tints.
const rgba = (hex, a) => {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const grid = (min) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))`,
  gap: 12,
});

const iconChip = (color) => ({
  width: 34,
  height: 34,
  borderRadius: 10,
  background: rgba(color, 0.12),
  color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

// ── KPI card: tinted icon chip, big tabular value, caption ──
const KpiCard = ({ icon, color, label, value, unit, sub, delay = 0 }) => (
  <div
    className="csd-card csd-in"
    style={{
      background: `linear-gradient(180deg, ${rgba(color, 0.05)} 0%, #ffffff 55%)`,
      border: `1px solid ${SLATE.line}`,
      borderRadius: 14,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      animationDelay: `${delay}ms`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: '0.64rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: SLATE.faint, fontWeight: 700 }}>
        {label}
      </span>
      <span style={iconChip(color)}>{icon}</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      <span style={{ fontSize: '1.75rem', fontWeight: 800, color: SLATE.ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
      {unit && <span style={{ fontSize: '0.8rem', fontWeight: 700, color: SLATE.muted }}>{unit}</span>}
    </div>
    {sub && <span style={{ fontSize: '0.66rem', color: SLATE.faint, fontWeight: 600 }}>{sub}</span>}
  </div>
);

// ── Power-factor ring gauge (SVG) ──
const PfRing = ({ value, delay = 0 }) => {
  const R = 30;
  const C = 2 * Math.PI * R;
  const mag = value === null ? 0 : Math.abs(value);
  const color = value === null ? SLATE.line : mag >= 0.9 ? '#16a34a' : mag >= 0.8 ? '#d97706' : '#dc2626';
  const dash = C * mag;
  return (
    <div
      className="csd-card csd-in"
      style={{
        background: '#fff',
        border: `1px solid ${SLATE.line}`,
        borderRadius: 14,
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        animationDelay: `${delay}ms`,
      }}
    >
      <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
        <svg width="72" height="72" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r={R} fill="none" stroke={SLATE.line2} strokeWidth="7" />
          <circle
            cx="36" cy="36" r={R} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={`${dash} ${C}`} transform="rotate(-90 36 36)"
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '1rem', fontWeight: 800, color: SLATE.ink, fontVariantNumeric: 'tabular-nums' }}>
            {value === null ? '—' : value.toFixed(2)}
          </span>
        </div>
      </div>
      <div>
        <div style={{ fontSize: '0.64rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: SLATE.faint, fontWeight: 700 }}>
          Power Factor
        </div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color, marginTop: 2 }}>
          {value === null ? 'No signal' : mag >= 0.9 ? 'Excellent' : mag >= 0.8 ? 'Fair' : 'Poor'}
        </div>
      </div>
    </div>
  );
};

// ── Phase current card with peak-utilisation bar ──
const PhaseCurrentCard = ({ phase, color, value, peak, delay = 0 }) => {
  const v = Number(value) || 0;
  const pk = Number(peak) || 0;
  const pct = pk > 0 ? Math.max(0, Math.min((v / pk) * 100, 100)) : 0;
  return (
    <div className="csd-card csd-in" style={{ background: '#fff', border: `1px solid ${SLATE.line}`, borderLeft: `3px solid ${color}`, borderRadius: 12, padding: 14, animationDelay: `${delay}ms` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: color, color: '#fff', fontWeight: 800, fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {phase}
        </span>
        <span style={{ fontSize: '0.68rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: SLATE.faint, fontWeight: 700 }}>
          Phase {phase}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: SLATE.ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{n2(value)}</span>
        <span style={{ fontSize: '0.74rem', fontWeight: 700, color: SLATE.muted }}>A</span>
      </div>
      <div style={{ marginTop: 10, height: 6, borderRadius: 999, background: SLATE.line2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: color, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ marginTop: 5, fontSize: '0.63rem', color: SLATE.faint, fontWeight: 600 }}>
        Peak today: {n2(peak)} A
      </div>
    </div>
  );
};

// ── Phase voltage card with low/high range track ──
const PhaseVoltageCard = ({ phase, color, value, high, low, delay = 0 }) => {
  const v = Number(value) || 0;
  const lo = Number(low);
  const hi = Number(high);
  let markerPct = null;
  if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
    markerPct = Math.max(0, Math.min(((v - lo) / (hi - lo)) * 100, 100));
  }
  return (
    <div className="csd-card csd-in" style={{ background: '#fff', border: `1px solid ${SLATE.line}`, borderLeft: `3px solid ${color}`, borderRadius: 12, padding: 14, animationDelay: `${delay}ms` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: color, color: '#fff', fontWeight: 800, fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {phase}
        </span>
        <span style={{ fontSize: '0.68rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: SLATE.faint, fontWeight: 700 }}>
          Phase {phase}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: SLATE.ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{n2(value)}</span>
        <span style={{ fontSize: '0.74rem', fontWeight: 700, color: SLATE.muted }}>V</span>
      </div>
      <div style={{ position: 'relative', marginTop: 10, height: 6, borderRadius: 999, background: `linear-gradient(90deg, ${rgba('#2563eb', 0.25)}, ${rgba('#dc2626', 0.25)})` }}>
        {markerPct !== null && (
          <span style={{ position: 'absolute', top: -2, left: `calc(${markerPct}% - 5px)`, width: 10, height: 10, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
        )}
      </div>
      <div style={{ marginTop: 5, display: 'flex', justifyContent: 'space-between', fontSize: '0.63rem', color: SLATE.faint, fontWeight: 600 }}>
        <span>L {n2(low)}</span>
        <span>H {n2(high)}</span>
      </div>
    </div>
  );
};

// ── Totalizer card ──
const TotalizerCard = ({ icon, color, label, value, unit, delay = 0 }) => (
  <div className="csd-card csd-in" style={{ background: '#fff', border: `1px solid ${SLATE.line}`, borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 14, animationDelay: `${delay}ms` }}>
    <span style={{ ...iconChip(color), width: 42, height: 42, borderRadius: 12 }}>{icon}</span>
    <div>
      <div style={{ fontSize: '0.64rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: SLATE.faint, fontWeight: 700 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 2 }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: SLATE.ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <span style={{ fontSize: '0.76rem', fontWeight: 700, color: SLATE.muted }}>{unit}</span>
      </div>
    </div>
  </div>
);

const CurrentSensorDashboard = ({ sensor }) => {
  const [summary, setSummary] = useState(null);
  const [summaryAt, setSummaryAt] = useState(null);

  const deviceCode = sensor?.device_code;
  const subId = sensor?.sensor_sub_id;
  const cs = sensor?.currentSensorData || {};

  useEffect(() => {
    if (!deviceCode || !subId) return undefined;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/current-sensor-logs/summary`, {
          params: { deviceCode, subId },
        });
        if (!cancelled && data?.success) {
          setSummary(data);
          setSummaryAt(new Date());
        }
      } catch (_) { /* card falls back to 0.00 */ }
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [deviceCode, subId]);

  if (!sensor) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: SLATE.faint, fontSize: '0.9rem' }}>
        No current sensor is configured on this device.
      </div>
    );
  }

  // ── Metric maths (identical to Aquabrim) ──
  const apparentTotalizer = (cs.energyMeterTotalizerInUnit || 0) + (cs.totalizer1 || 0);
  const activeTotalizer = (cs.totalizerActiveA || 0) + (cs.totalizerActiveB || 0);
  const activePower = cs.activePower || 0;
  const reactivePower = cs.reactivePower || 0;
  const powerFactor = (() => {
    const p = Number(activePower) || 0;
    const q = Number(reactivePower) || 0;
    const denom = Math.sqrt(p * p + q * q);
    return denom === 0 ? null : Math.max(-1, Math.min(1, p / denom));
  })();

  const peakCurrent = summary?.today?.peakCurrent || {};
  const voltageStats = summary?.today?.voltage || {};
  const peakPower = summary?.today?.peakPower || {};
  const todaysUnits = summary?.today?.consumption ?? 0;

  return (
    <div>
      <style>{`
        .csd-card { transition: transform .18s ease, box-shadow .18s ease; }
        .csd-card:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(15,23,42,0.10); }
        @keyframes csdIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .csd-in { animation: csdIn .4s ease both; }
      `}</style>

      {/* ── Live Overview (KPI hero) ── */}
      <SectionCard
        icon={<FaBolt size={16} color={PHASE.active} />}
        title="Live Overview"
        subtitle={`Updated ${rel(cs.activePowerLastUpdated)}`}
      >
        <div style={grid('190px')}>
          <KpiCard icon={<FaBolt size={15} />} color={PHASE.active} label="Active Power" value={n2(activePower)} unit="kW" sub={`Peak today ${n2(peakPower.active)} kW`} delay={0} />
          <KpiCard icon={<FaWaveSquare size={15} />} color={PHASE.reactive} label="Reactive Power" value={n2(reactivePower)} unit="kVAR" sub={`Peak today ${n2(peakPower.reactive)} kVAR`} delay={60} />
          <PfRing value={powerFactor} delay={120} />
          <KpiCard icon={<FaLeaf size={15} />} color={PHASE.avg} label="Today's Units" value={n2(todaysUnits)} unit="kVAh" sub={`As of ${rel(summaryAt)}`} delay={180} />
        </div>
      </SectionCard>

      {/* ── Energy Totalizers ── */}
      <SectionCard
        icon={<FaTachometerAlt size={16} color={PHASE.B} />}
        title="Energy Totalizers"
        subtitle={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FaClock size={10} /> {rel(cs.totalizer1LastUpdated)}</span>}
      >
        <div style={grid('220px')}>
          <TotalizerCard icon={<FaPlug size={16} />} color={PHASE.B} label="Apparent Energy" value={n2(apparentTotalizer)} unit="kVAHr" delay={0} />
          <TotalizerCard icon={<FaBolt size={16} />} color={PHASE.reactive} label="Active Energy" value={n2(activeTotalizer)} unit="kWHr" delay={60} />
        </div>
      </SectionCard>

      {/* ── 3-Phase Current ── */}
      <SectionCard
        icon={<FaWaveSquare size={16} color={PHASE.R} />}
        title="3-Phase Current"
        subtitle={`Updated ${rel(cs.currentRLastUpdated)}`}
      >
        <div style={grid('180px')}>
          <PhaseCurrentCard phase="R" color={PHASE.R} value={cs.currentR} peak={peakCurrent.R} delay={0} />
          <PhaseCurrentCard phase="Y" color={PHASE.Y} value={cs.currentY} peak={peakCurrent.Y} delay={60} />
          <PhaseCurrentCard phase="B" color={PHASE.B} value={cs.currentB} peak={peakCurrent.B} delay={120} />
        </div>
      </SectionCard>

      {/* ── 3-Phase Voltage ── */}
      <SectionCard
        icon={<FaPlug size={16} color={PHASE.B} />}
        title="3-Phase Voltage"
        subtitle={`Updated ${rel(cs.voltageRLastUpdated)}`}
      >
        <div style={grid('180px')}>
          <PhaseVoltageCard phase="R" color={PHASE.R} value={cs.voltageR} high={voltageStats?.R?.high} low={voltageStats?.R?.low} delay={0} />
          <PhaseVoltageCard phase="Y" color={PHASE.Y} value={cs.voltageY} high={voltageStats?.Y?.high} low={voltageStats?.Y?.low} delay={60} />
          <PhaseVoltageCard phase="B" color={PHASE.B} value={cs.voltageB} high={voltageStats?.B?.high} low={voltageStats?.B?.low} delay={120} />
        </div>
      </SectionCard>

      {/* ── Graphs + Logs ── */}
      <CurrentSensorGraphs sensor={sensor} />
      <CurrentSensorLogs sensor={sensor} />
    </div>
  );
};

export default CurrentSensorDashboard;
